import type { UploadChangeParam } from 'antd/es/upload';
import type { UploadFile } from 'antd/es/upload/interface';
import type { AdminProduct, AdminProductFormValues, MaterialIconOption, ProductTypeOption, SizeOption } from '../interfaces';

export const ADMIN_PRODUCTS_PAGE_SIZE = 8;

const normalizeTypeValue = (value: string) =>
  value
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/\s+/g, '')
    .replace(/bracelets/g, 'bracelet')
    .replace(/rings/g, 'rings')
    .replace(/necklace/g, 'necklaces');

export const getFilteredSizes = (
  sizes: SizeOption[],
  selectedGender?: SizeOption['gender'],
  selectedTypeName?: string,
  requiresSizes = true
) =>
  requiresSizes
    ? sizes.filter((size) => {
    const matchesGender = selectedGender ? size.gender === selectedGender : true;
    const matchesType = selectedTypeName
      ? normalizeTypeValue(size.productType) === normalizeTypeValue(selectedTypeName)
      : true;

    return matchesGender && matchesType;
  })
    : [];

const getAllowedSizeIds = (
  sizes: SizeOption[],
  gender: AdminProductFormValues['gender'],
  productTypeName: string,
  requiresSizes: boolean
) =>
  new Set(
    getFilteredSizes(sizes, gender, productTypeName, requiresSizes).map((size) => size.id)
  );

const assertSizeIdsMatch = (sizeIds: string[] = [], allowedSizeIds: Set<string>, label: string) => {
  const invalidSizeIds = sizeIds.filter((sizeId) => !allowedSizeIds.has(sizeId));

  if (invalidSizeIds.length) {
    throw new Error(`${label} contains sizes that do not match the selected product type and gender.`);
  }
};

const getVariantStock = (
  variantStocks: AdminProductFormValues['materials'][number]['variantStocks'] = {},
  sizeId: string
) => Number(variantStocks[sizeId]);

export const validateCreateAdminProductValues = (
  values: AdminProductFormValues,
  sizes: SizeOption[],
  productTypes: ProductTypeOption[]
) => {
  const productType = productTypes.find((type) => type.id === values.productTypeId);

  if (!productType) {
    throw new Error('Select a product type.');
  }

  if (!values.materials?.length) {
    throw new Error('At least one material is required.');
  }

  const requiresSizes = productType.requiresSizes !== false;
  const allowedSizeIds = getAllowedSizeIds(sizes, values.gender, productType.name, requiresSizes);
  const defaultSizeIds = values.sizeIds || [];

  if (requiresSizes && !defaultSizeIds.length) {
    throw new Error(`${productType.name} products require a size grid.`);
  }

  if (!requiresSizes && defaultSizeIds.length) {
    throw new Error(`${productType.name} products must not use selectable sizes.`);
  }

  assertSizeIdsMatch(defaultSizeIds, allowedSizeIds, 'Default size grid');

  values.materials.forEach((material, index) => {
    const label = material.materialName || `Material ${index + 1}`;
    const materialSizeIds = material.sizeIds?.length ? material.sizeIds : defaultSizeIds;

    if (!material.materialName?.trim()) {
      throw new Error(`Material ${index + 1} requires a name.`);
    }

    if (!Number.isFinite(Number(material.price)) || Number(material.price) <= 0) {
      throw new Error(`${label} requires a price greater than 0.`);
    }

    if (!requiresSizes && (!Number.isFinite(Number(material.stockQuantity)) || Number(material.stockQuantity) < 0)) {
      throw new Error(`${label} requires stock quantity.`);
    }

    if (!material.images?.length) {
      throw new Error(`${label} requires at least one image.`);
    }

    if (requiresSizes && !materialSizeIds.length) {
      throw new Error(`${label} requires at least one available size.`);
    }

    if (requiresSizes) {
      materialSizeIds.forEach((sizeId) => {
        const size = sizes.find((item) => item.id === sizeId);
        const variantStock = getVariantStock(material.variantStocks, sizeId);

        if (!Number.isFinite(variantStock) || variantStock < 0) {
          throw new Error(`${label} requires stock for ${size?.label || 'each size'}.`);
        }
      });
    }

    if (!requiresSizes && materialSizeIds.length) {
      throw new Error(`${label} must not use selectable sizes.`);
    }

    assertSizeIdsMatch(materialSizeIds, allowedSizeIds, `${label} size grid`);
  });
};

export const normalizeUploadEvent = (
  event: UploadChangeParam<UploadFile> | UploadFile[]
) => {
  if (Array.isArray(event)) {
    return event;
  }

  return event?.fileList || [];
};

export const getDefaultMaterialValues = (materialTemplates: MaterialIconOption[]) =>
  materialTemplates.map((item) => ({
    materialName: item.materialName,
    price: 0,
    stockQuantity: 0,
    sizeIds: [],
    variantStocks: {},
    materialIconId: item.id,
    images: [],
  }));

export const createDraftStockMap = (products: AdminProduct[]) =>
  Object.fromEntries(
    products.flatMap((product) =>
      product.materialPrices.flatMap((material) =>
        (material.variantInventories || [])
          .filter((variant) => !variant.product?.id || variant.product.id === product.id)
          .map((variant) => [variant.id, variant.stockQuantity])
      )
    )
  );

export const createDraftPriceMap = (products: AdminProduct[]) =>
  Object.fromEntries(
    products.flatMap((product) =>
      product.materialPrices.map((material) => [material.id, material.price])
    )
  );

export const updateVariantStockInList = (
  products: AdminProduct[],
  variantInventoryId: string,
  stockQuantity: number
) =>
  products.map((product) => ({
    ...product,
    materialPrices: product.materialPrices.map((material) =>
      ({
        ...material,
        variantInventories: material.variantInventories?.map((variant) =>
          variant.id === variantInventoryId
            ? { ...variant, stockQuantity }
            : variant
        ),
      })
    ),
  }));

export const updateVariantAvailabilityInList = (
  products: AdminProduct[],
  variantInventoryId: string,
  isAvailable: boolean
) =>
  products.map((product) => ({
    ...product,
    materialPrices: product.materialPrices.map((material) => ({
      ...material,
      variantInventories: material.variantInventories?.map((variant) =>
        variant.id === variantInventoryId
          ? { ...variant, isAvailable }
          : variant
      ),
    })),
  }));

export const updateProductPriceInList = (
  products: AdminProduct[],
  materialPriceId: string,
  price: number
) =>
  products.map((product) => ({
    ...product,
    materialPrices: product.materialPrices.map((material) =>
      material.id === materialPriceId
        ? { ...material, price }
        : material
    ),
  }));

export const updateMaterialSizesInList = (
  products: AdminProduct[],
  materialPriceId: string,
  sizes: SizeOption[]
) =>
  products.map((product) => ({
    ...product,
    materialPrices: product.materialPrices.map((material) =>
      material.id === materialPriceId
        ? { ...material, sizes }
        : material
    ),
  }));

export const mergeSearchedProducts = (
  baseProducts: AdminProduct[],
  searchedProducts: AdminProduct[]
) => {
  const loadedById = new Map(baseProducts.map((product) => [product.id, product]));

  return [
    ...searchedProducts,
    ...baseProducts.filter(
      (product) => !searchedProducts.some((searched) => searched.id === product.id)
    ),
  ].map((product) => loadedById.get(product.id) || product);
};
